import { create } from 'zustand';
import AsyncStorage from '@react-native-async-storage/async-storage';

import {
  isLayoutId,
  normalizeSlots,
  type LayoutId,
  type Slots,
} from './layouts';

/**
 * Что помнит просмотр камер между запусками.
 *
 * Регистратор помнит «вид» сам: охранник один раз разложил камеры так, как стоят двери,
 * и каждое утро видит ровно это. Кабинет без этого раскладывал бы камеры заново при
 * каждом заходе — и каждый раз в порядке каталога, а не в порядке объекта.
 *
 *   views    — последний вид по компании: раскладка и камеры в окнах;
 *   presets  — сохранённые виды по компании («Склад», «Входы», «Ночь») — как «Виды»
 *              в iVMS: один щелчок, и раскладка с камерами на месте;
 *   prefs    — привычки смотрящего, общие для всех компаний: открыт ли список камер,
 *              как он сгруппирован, экономия трафика, шаг карусели.
 *
 * Хранилище то же, что у темы и языка (на вебе — `localStorage`). Запись, которая не
 * удалась, стоит настройки при СЛЕДУЮЩЕМ запуске, а не сейчас, поэтому ошибки молчат.
 */
export type SavedView = { layout: LayoutId; slots: Slots };

export type Preset = SavedView & { id: string; name: string };

export type GroupBy = 'device' | 'department';

export type Prefs = {
  panelOpen: boolean;
  groupBy: GroupBy;
  /**
   * Экономия трафика: окна за развёрнутым соседом не играют. Свернуть окно обратно —
   * тогда новое соединение, секунда-две ожидания.
   */
  economy: boolean;
  /** Шаг карусели, секунды. */
  carouselSeconds: number;
  /**
   * Телефон: выбранная камера крупно сверху, остальные миниатюрами под ней — вместо
   * сетки, где на 360pt каждое окно размером с почтовую марку (владелец, 2026-10-22).
   */
  phoneFocus: boolean;
  /**
   * Пульт PTZ у любой камеры, про которую не сказано, что она неповоротная. Каталог
   * флаг `ptz` пока не отдаёт (CABINET-BACKEND-ASKS.md §6), и без этой кнопки пульта
   * не было бы нигде.
   */
  ptzOpen: boolean;
  /** Пульт свёрнут до шапки — на телефоне он иначе съедает пол-экрана. */
  ptzCollapsed: boolean;
};

export const CAROUSEL_STEPS = [10, 20, 30, 60] as const;

const DEFAULT_PREFS: Prefs = {
  panelOpen: true,
  groupBy: 'device',
  economy: false,
  carouselSeconds: 20,
  phoneFocus: true,
  ptzOpen: false,
  ptzCollapsed: false,
};

const STORAGE_KEY = 'samvision.live-view';
const PRESETS_KEY = 'samvision.live-view.presets';
const PREFS_KEY = 'samvision.live-view.prefs';

type ViewStore = {
  views: Record<string, SavedView>;
  presets: Record<string, Preset[]>;
  prefs: Prefs;
  hydrated: boolean;
  hydrate: () => Promise<void>;
  save: (companyId: string, view: SavedView) => void;
  savePreset: (companyId: string, name: string, view: SavedView) => Preset;
  deletePreset: (companyId: string, id: string) => void;
  setPrefs: (patch: Partial<Prefs>) => void;
};

function readView(value: unknown): SavedView | null {
  const v = value as { layout?: unknown; slots?: unknown } | null;
  return v && isLayoutId(v.layout)
    ? { layout: v.layout, slots: normalizeSlots(v.slots) }
    : null;
}

function readJson(raw: string | null): unknown {
  try {
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function write(key: string, value: unknown): void {
  void AsyncStorage.setItem(key, JSON.stringify(value)).catch(() => {});
}

/** Привести прочитанное к `Prefs`: чужое и устаревшее молча становится умолчанием. */
export function readPrefs(value: unknown): Prefs {
  const raw = (value ?? {}) as Partial<Record<keyof Prefs, unknown>>;
  return {
    panelOpen:
      typeof raw.panelOpen === 'boolean'
        ? raw.panelOpen
        : DEFAULT_PREFS.panelOpen,
    groupBy:
      raw.groupBy === 'device' || raw.groupBy === 'department'
        ? raw.groupBy
        : DEFAULT_PREFS.groupBy,
    economy:
      typeof raw.economy === 'boolean' ? raw.economy : DEFAULT_PREFS.economy,
    carouselSeconds: (CAROUSEL_STEPS as readonly unknown[]).includes(
      raw.carouselSeconds,
    )
      ? (raw.carouselSeconds as number)
      : DEFAULT_PREFS.carouselSeconds,
    phoneFocus:
      typeof raw.phoneFocus === 'boolean'
        ? raw.phoneFocus
        : DEFAULT_PREFS.phoneFocus,
    ptzOpen:
      typeof raw.ptzOpen === 'boolean' ? raw.ptzOpen : DEFAULT_PREFS.ptzOpen,
    ptzCollapsed:
      typeof raw.ptzCollapsed === 'boolean'
        ? raw.ptzCollapsed
        : DEFAULT_PREFS.ptzCollapsed,
  };
}

export const useLiveViewStore = create<ViewStore>()((set, get) => ({
  views: {},
  presets: {},
  prefs: DEFAULT_PREFS,
  hydrated: false,

  hydrate: async () => {
    if (get().hydrated) {
      return;
    }
    try {
      const [rawViews, rawPresets, rawPrefs] = await Promise.all([
        AsyncStorage.getItem(STORAGE_KEY),
        AsyncStorage.getItem(PRESETS_KEY),
        AsyncStorage.getItem(PREFS_KEY),
      ]);

      const views: Record<string, SavedView> = {};
      const parsedViews = readJson(rawViews);
      if (parsedViews && typeof parsedViews === 'object') {
        for (const [companyId, value] of Object.entries(
          parsedViews as Record<string, unknown>,
        )) {
          const v = readView(value);
          if (v) {
            views[companyId] = v;
          }
        }
      }

      const presets: Record<string, Preset[]> = {};
      const parsedPresets = readJson(rawPresets);
      if (parsedPresets && typeof parsedPresets === 'object') {
        for (const [companyId, list] of Object.entries(
          parsedPresets as Record<string, unknown>,
        )) {
          if (Array.isArray(list)) {
            presets[companyId] = list.flatMap(item => {
              const v = readView(item);
              const it = item as { id?: unknown; name?: unknown };
              return v &&
                typeof it.id === 'string' &&
                typeof it.name === 'string'
                ? [{ ...v, id: it.id, name: it.name }]
                : [];
            });
          }
        }
      }

      // Сохранённое на этом запуске раньше чтения — новее прочитанного.
      set({
        views: { ...views, ...get().views },
        presets: { ...presets, ...get().presets },
        prefs: readPrefs(readJson(rawPrefs)),
        hydrated: true,
      });
    } catch {
      set({ hydrated: true });
    }
  },

  save: (companyId, view) => {
    const views = { ...get().views, [companyId]: view };
    set({ views });
    write(STORAGE_KEY, views);
  },

  savePreset: (companyId, name, view) => {
    const list = get().presets[companyId] ?? [];
    // Имя — ключ для человека: сохранить «Склад» второй раз значит обновить его.
    const existing = list.find(
      x => x.name.trim().toLowerCase() === name.trim().toLowerCase(),
    );
    const preset: Preset = {
      layout: view.layout,
      slots: view.slots.slice(),
      id: existing?.id ?? `v${Date.now().toString(36)}`,
      name: name.trim(),
    };
    const next = existing
      ? list.map(x => (x.id === existing.id ? preset : x))
      : [...list, preset];
    const presets = { ...get().presets, [companyId]: next };
    set({ presets });
    write(PRESETS_KEY, presets);
    return preset;
  },

  deletePreset: (companyId, id) => {
    const list = (get().presets[companyId] ?? []).filter(x => x.id !== id);
    const presets = { ...get().presets, [companyId]: list };
    set({ presets });
    write(PRESETS_KEY, presets);
  },

  setPrefs: patch => {
    const prefs = { ...get().prefs, ...patch };
    set({ prefs });
    write(PREFS_KEY, prefs);
  },
}));
